<template>
  <div class="align-center justify-center" :data-cy="dataCy">
    <v-form ref="form" validate-on="lazy" data-cy="cardApplication-custodian-dialog">
      <v-card-text class="card-text">
        <v-row>
          <v-col class="pt-0">
            <v-text-field
              v-model="custodian.firstName"
              :rules="validations.firstName"
              :label="$t(`${i18KeyGroup}.firstName`)"
              :aria-label="$t(`${i18KeyGroup}.firstName`)"
              :data-cy="`${dataCy}-firstName`"
              :readonly="isFormDisabled"
              variant="underlined"
              autocomplete="first-name"
              required
            />
            <v-text-field
              v-model="custodian.lastName"
              :rules="validations.lastName"
              :label="$t(`${i18KeyGroup}.lastName`)"
              :aria-label="$t(`${i18KeyGroup}.lastName`)"
              :data-cy="`${dataCy}-lastName`"
              :readonly="isFormDisabled"
              variant="underlined"
              autocomplete="last-name"
              required
            />
            <v-text-field
              v-model="custodian.birthDay"
              :placeholder="dateFormatPlaceholder"
              :label="$t(`${i18KeyGroup}.birthDay`)"
              :aria-label="$t(`${i18KeyGroup}.birthDay`)"
              :rules="validations.birthDay"
              :clearable="!isFormDisabled"
              :readonly="isFormDisabled"
              :data-cy="`${dataCy}-birthDay`"
              variant="underlined"
              autocomplete="birthday"
              required
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col :cols="$vuetify.display.mobile ? 12 : 4" class="py-1">
            <v-text-field
              v-model="custodian.address.street"
              :rules="validations.addressStreet"
              :label="$t(`${i18KeyGroup}.addressStreet`)"
              :aria-label="$t(`${i18KeyGroup}.addressStreet`)"
              :readonly="isFormDisabled"
              :data-cy="`${dataCy}-addressStreet`"
              variant="underlined"
              autocomplete="street"
              :required="systemConfigurationModule.configuration.addressStreetRequired"
            />
          </v-col>
          <v-col :cols="$vuetify.display.mobile ? 12 : 4" class="py-1">
            <v-text-field
              v-model="custodian.address.streetNumber"
              :rules="validations.addressStreetNumber"
              :label="$t(`${i18KeyGroup}.addressStreetNumber`)"
              :aria-label="$t(`${i18KeyGroup}.addressStreetNumber`)"
              :readonly="isFormDisabled"
              :data-cy="`${dataCy}-addressStreetNumber`"
              variant="underlined"
              autocomplete="street-number"
              :required="systemConfigurationModule.configuration.addressStreetNumberRequired"
            />
          </v-col>
          <v-col :cols="$vuetify.display.mobile ? 12 : 4" class="py-1">
            <v-text-field
              v-model="custodian.address.city"
              :rules="validations.addressCity"
              :label="$t(`${i18KeyGroup}.addressCity`)"
              :aria-label="$t(`${i18KeyGroup}.addressCity`)"
              :readonly="isFormDisabled"
              :data-cy="`${dataCy}-addressCity`"
              variant="underlined"
              autocomplete="city"
              :required="systemConfigurationModule.configuration.addressCityRequired"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col :cols="$vuetify.display.mobile ? 12 : 4" class="py-1">
            <v-text-field
              v-model="custodian.address.zip"
              :rules="validations.addressZipCode"
              :label="$t(`${i18KeyGroup}.addressZipCode`)"
              :aria-label="$t(`${i18KeyGroup}.addressZipCode`)"
              :aria-disabled="isFormDisabled"
              :readonly="isFormDisabled"
              :data-cy="`${dataCy}-addressZipCode`"
              variant="underlined"
              autocomplete="postal-code"
              :required="systemConfigurationModule.configuration.addressZipCodeRequired"
            />
          </v-col>
          <v-col :cols="$vuetify.display.mobile ? 12 : 4" class="py-1">
            <v-text-field
              v-model="custodian.email"
              :rules="validations.email"
              :label="$t(`${i18KeyGroup}.email`)"
              :aria-label="$t(`${i18KeyGroup}.email`)"
              :readonly="isFormDisabled"
              :aria-disabled="isFormDisabled"
              :data-cy="`${dataCy}-email`"
              variant="underlined"
              autocomplete="email"
              type="email"
              @change="emailOrPhoneChanged"
            />
          </v-col>
          <v-col :cols="$vuetify.display.mobile ? 12 : 4" class="py-1">
            <v-text-field
              v-model="custodian.phoneNumber"
              :rules="validations.phone"
              :label="$t(`${i18KeyGroup}.phone`)"
              :aria-label="$t(`${i18KeyGroup}.phone`)"
              :readonly="isFormDisabled"
              :data-cy="`${dataCy}-phone`"
              variant="underlined"
              autocomplete="phoneNumber"
              @keypress="allowPhoneNumber"
              @change="emailOrPhoneChanged"
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-form>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Ref, Vue, Watch } from 'vue-facing-decorator';
import {
  dateValidator,
  emailValidator,
  maxLengthValidator,
  requiredValidator,
  requiredValidatorIf,
} from '@/utils/validators';
import { CardApplicationDto, CustomerDetailVm, CustomerDto } from '@/api';
import cardApplicationModule from '@/store/modules/cardApplication';
import KeyboardEventHelper from '@common/utils/keyboardEventHelper';
import { DATE_BIRTHDAY_FORMAT, MIN_DAY_OF_BIRTHDAY, toMomentDateFormat } from '@/utils/dateTime';
import { FormInterface } from '@/components/common/FormInterface';
import debounce from 'lodash/debounce';
import Table from '@/constants/Table';
import { CustomerUniqueCheckType } from '@/components/cardApplication/customerUniqueCheckType';
import SystemConfigurationModule from '@/store/modules/systemConfiguration';
import { ValidationInterface } from '@/components/common/ValidationInterface';
import { subYears } from 'date-fns';
import { InvalidFieldNames } from '@/utils/i18n';

@Component({
  expose: ['form', 'validate', 'stepActive'],
})
export default class CustodianForm extends Vue implements ValidationInterface {
  @Prop({ required: false, type: Boolean, default: false })
  readonly disabled!: boolean;

  @Prop({ required: false, type: Boolean, default: false })
  readonly stepActive!: boolean;

  @Prop({ required: false, type: Function, default: undefined })
  readonly checkIfUserIsUnique!: (dto: CustomerDto, type: CustomerUniqueCheckType) => void;

  @Ref()
  readonly form!: FormInterface;

  i18KeyGroup = 'cardApplication.custodianData';
  dataCy = 'cardApplication-custodian';

  cardApplicationModule = cardApplicationModule;
  systemConfigurationModule = SystemConfigurationModule;
  dateFormat = DATE_BIRTHDAY_FORMAT;
  dateFormatPlaceholder = toMomentDateFormat(this.dateFormat);

  allowPhoneNumber = KeyboardEventHelper.allowPhoneNumber;

  validations = {
    firstName: [
      (v: string) => requiredValidator(v, InvalidFieldNames.firstName),
      (v: string) => maxLengthValidator(v, 50),
    ],
    lastName: [
      (v: string) => requiredValidator(v, InvalidFieldNames.lastName),
      (v: string) => maxLengthValidator(v, 50),
    ],
    birthDay: [
      (v: string) =>
        dateValidator(
          v,
          this.dateFormat,
          MIN_DAY_OF_BIRTHDAY,
          this.getMaxDayOfBirthDay().toISOString().substring(0, 10),
          true,
        ),
    ],
    addressStreet: [
      (v: string) =>
        requiredValidatorIf(
          v,
          () => this.systemConfigurationModule.configuration.addressStreetRequired,
          InvalidFieldNames.street,
        ),
      (v: string) => maxLengthValidator(v, 50),
    ],
    addressStreetNumber: [
      (v: string) =>
        requiredValidatorIf(
          v,
          () => this.systemConfigurationModule.configuration.addressStreetNumberRequired,
          InvalidFieldNames.streetNumber,
        ),
      (v: string) => maxLengthValidator(v, 15),
    ],
    addressCity: [
      (v: string) =>
        requiredValidatorIf(
          v,
          () => this.systemConfigurationModule.configuration.addressCityRequired,
          InvalidFieldNames.city,
        ),
      (v: string) => maxLengthValidator(v, 50),
    ],
    addressZipCode: [
      (v: string) =>
        requiredValidatorIf(
          v,
          () => this.systemConfigurationModule.configuration.addressZipCodeRequired,
          InvalidFieldNames.zip,
        ),
      (v: string) => maxLengthValidator(v, 6),
    ],
    email: [(v: string) => emailValidator(v), (v: string) => maxLengthValidator(v, 50)],
    phone: [(v: string) => maxLengthValidator(v, 20)],
  };

  set custodian(data: CustomerDto) {
    this.cardApplicationModule.setCustodianData(data);
  }

  get custodian(): CustomerDto {
    return this.cardApplicationModule.custodianData;
  }

  get customer(): CustomerDetailVm {
    return this.cardApplicationModule.customerData;
  }

  get cardData(): CardApplicationDto {
    return this.cardApplicationModule.cardData;
  }

  getMaxDayOfBirthDay() {
    return subYears(new Date(), 18);
  }

  emailOrPhoneChanged() {
    this.cardData.contactMeIfCardFound =
      !!this.customer.email || !!this.customer.phoneNumber || !!this.custodian.email || !!this.custodian.phoneNumber;
  }

  get isFormDisabled() {
    return this.disabled || this.custodian.id !== '';
  }

  skip() {
    this.cardApplicationModule.resetCustodian();
    this.form.resetValidation();
    return true;
  }

  @Watch('custodian.birthDay')
  async birthDayChanged() {
    await this.checkIfIsUniqueDebounced();
  }

  @Watch('custodian.firstName')
  firstNameChanged() {
    this.checkIfIsUniqueDebounced();
  }

  @Watch('custodian.lastName')
  lastNameChanged() {
    this.checkIfIsUniqueDebounced();
  }

  checkIfIsUnique() {
    if (this.custodian.id === '') {
      this.checkIfUserIsUnique(this.custodian, CustomerUniqueCheckType.Custodian);
    }
  }

  checkIfIsUniqueDebounced = debounce(this.checkIfIsUnique, Table.DEFAULT_TABLE_DEBOUNCE_TIME);

  async validate() {
    if (this.disabled) {
      return true; // skip validation
    }

    return (await this.form.validate()).valid;
  }
}
</script>

<style scoped lang="scss">
.card-text {
  height: 100%;
  overflow: auto;
}
</style>
